import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  DashboardOutlined,
  BusinessOutlined,
  PeopleOutlined,
  AccountCircle,
  DocumentScannerOutlined,
  AssignmentOutlined,
} from '@mui/icons-material';
import { logout } from '../../redux/slices/authSlice';

import logo from '../../assets/images/CCO-Logo1.jpg';

// Import pages
import Dashboard from '../../pages/Dashboard';
import Companies from '../../pages/companies/Companies';
import Users from '../../pages/users/Users';
import Profile from '../../pages/Profile';
import KPIPage from '../../pages/kpi/KPIPage';
import MyKPIsPage from '../../pages/kpi/MyKPIsPage';
import KPIDetailsPage from '../../pages/kpi/KPIDetailsPage';

const drawerWidth = 240;

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardOutlined />, path: 'dashboard', roles: ['superAdmin', 'coach', 'companyAdmin', 'manager'] },
    { text: 'Users', icon: <PeopleOutlined />, path: 'users', roles: ['superAdmin', 'coach', 'companyAdmin', 'manager'] },
    { text: 'Companies', icon: <BusinessOutlined />, path: 'companies', roles: ['superAdmin', 'coach'] },
    { text: 'Create KPI', icon: <DocumentScannerOutlined />, path: 'create', roles: ['coach', 'companyAdmin', 'manager', 'employee'] },
    { text: 'My KPIs', icon: <AssignmentOutlined />, path: 'my-kpis', roles: ['coach','companyAdmin', 'manager', 'employee'] },
  ];

  const drawer = (
    <div>
      <List>
        {menuItems.map((item) => (
          (!item.roles || item.roles.includes(user?.role)) && (
            <ListItem
              button
              key={item.text}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          )
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar className="no-print" position="fixed" sx={{ bgcolor: '#1990bb', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <a href="https://cco-toolkit.com/" style={{ display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="KPI Logo" style={{ height: '40px', marginRight: '16px' }} />
          </a>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            KPI Management System
          </Typography>
          <Button
            color="inherit"
            onClick={handleMenuOpen}
            startIcon={
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircle />
              </Avatar>
            }
          >
            {user?.name}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => navigate('profile')}>Profile</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        <Box
          className="no-print"
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                mt: '64px',
                height: `calc(100% - 64px)`
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                mt: '64px',
                height: `calc(100% - 64px)`
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Routes>
            <Route index element={<Navigate to="dashboard" />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="companies" element={<Companies />} />
            <Route path="profile" element={<Profile />} />
            <Route path="create" element={<KPIPage />} />
            <Route path="my-kpis" element={<MyKPIsPage />} />
            <Route path="kpi/:id" element={<KPIDetailsPage />} />
            <Route path="*" element={<Navigate to="dashboard" />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
